import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, LanguageContext, API } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { BarChart3, Plus, Settings, XCircle } from 'lucide-react';

// Enhanced Reports Management Component with Agency Breakdown
const ReportsManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  const [reportType, setReportType] = useState('daily_sales');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedAgencies, setSelectedAgencies] = useState('all');
  const [groupByAgency, setGroupByAgency] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agencies, setAgencies] = useState([]);

  // Set default dates (last 30 days)
  useEffect(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    
    setStartDate(thirtyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);

    // Fetch agencies for filter
    const fetchAgencies = async () => {
      try {
        const response = await axios.get(`${API}/agencies`);
        setAgencies(response.data);
      } catch (error) {
        console.error('Error fetching agencies:', error);
      }
    };

    if (['super_admin', 'general_accountant'].includes(user?.role)) {
      fetchAgencies();
    }
  }, [user]);

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setError('يرجى تحديد تاريخ البداية والنهاية');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      let endpoint = '';
      let params = new URLSearchParams();

      // Common parameters
      if (reportType !== 'aging') {
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      }

      // Agency filtering (only for super admin and general accountant)
      if (['super_admin', 'general_accountant'].includes(user?.role)) {
        if (selectedAgencies !== 'all') {
          params.append('agency_ids', selectedAgencies);
        }
        params.append('group_by_agency', groupByAgency.toString());
      }

      switch (reportType) {
        case 'daily_sales':
          endpoint = 'reports/sales';
          params.append('report_type', 'daily');
          break;
        case 'monthly_sales':
          endpoint = 'reports/sales';
          params.append('report_type', 'monthly');
          break;
        case 'aging':
          endpoint = 'reports/aging';
          // Agency params already set above
          break;
        case 'summary':
          endpoint = 'reports/summary';
          break;
        default:
          throw new Error('نوع التقرير غير مدعوم');
      }

      console.log(`Generating ${reportType} report...`);
      console.log(`API call: ${API}/${endpoint}?${params.toString()}`);

      const response = await axios.get(`${API}/${endpoint}?${params.toString()}`);
      console.log('Report response:', response.data);
      
      setReportData(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.response?.data?.detail || 'فشل في إنتاج التقرير');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = () => {
    if (!reportData) return;

    try {
      let csvContent = "data:text/csv;charset=utf-8,\uFEFF"; // BOM for UTF-8
      
      if (reportData.group_by_agency && reportData.agencies_data) {
        // Agency-grouped export
        if (reportType === 'daily_sales' || reportType === 'monthly_sales') {
          csvContent += "الوكالة,التاريخ/الشهر,المبيعات (دج),عدد الحجوزات\n";
          
          reportData.agencies_data.forEach(agency => {
            agency.periods.forEach(period => {
              const date = reportType === 'monthly_sales' ? period.month : period.date;
              csvContent += `${agency.agency_name},${date},${period.sales},${period.bookings}\n`;
            });
            csvContent += `${agency.agency_name} - المجموع,,${agency.totals.sales},${agency.totals.bookings}\n\n`;
          });
          
          csvContent += `الإجمالي العام,,${reportData.grand_totals.sales},${reportData.grand_totals.bookings}\n`;
        } else if (reportType === 'aging') {
          csvContent += "الوكالة,العميل,رقم الفاتورة,المبلغ (دج),عدد الأيام\n";
          
          reportData.agencies_data.forEach(agency => {
            agency.invoices.forEach(invoice => {
              csvContent += `${agency.agency_name},${invoice.client},${invoice.invoice},${invoice.amount},${invoice.days}\n`;
            });
            csvContent += `${agency.agency_name} - المجموع,,,${agency.totals.amount},\n\n`;
          });
          
          csvContent += `الإجمالي العام,,,${reportData.grand_totals.amount},\n`;
        }
      } else {
        // Traditional export format
        if (reportType === 'daily_sales' || reportType === 'monthly_sales') {
          const dateLabel = reportType === 'monthly_sales' ? 'الشهر' : 'التاريخ';
          csvContent += `${dateLabel},المبيعات (دج),عدد الحجوزات\n`;
          reportData.data.forEach(row => {
            const date = reportType === 'monthly_sales' ? row.month : row.date;
            csvContent += `${date},${row.sales},${row.bookings}\n`;
          });
          csvContent += `الإجمالي,${reportData.totals.sales},${reportData.totals.bookings}\n`;
        } else if (reportType === 'aging') {
          csvContent += "العميل,رقم الفاتورة,المبلغ (دج),عدد الأيام\n";
          reportData.data.forEach(row => {
            csvContent += `${row.client},${row.invoice},${row.amount},${row.days}\n`;
          });
          csvContent += `الإجمالي,,${reportData.totals.amount},\n`;
        }
      }

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", `تقرير_${reportType}_${startDate}_${endDate}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Export error:', error);
      alert('فشل في تصدير التقرير');
    }
  };

  const getReportTypeOptions = () => [
    { value: 'daily_sales', label: '📈 تقرير المبيعات اليومية' },
    { value: 'monthly_sales', label: '📊 تقرير المبيعات الشهرية' },
    { value: 'aging', label: '⏰ تقرير أعمار الديون' },
    { value: 'summary', label: '📋 تقرير ملخص المبيعات' }
  ];

  const isAdminUser = ['super_admin', 'general_accountant'].includes(user?.role);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg p-6 shadow-sm border">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">📊 {t('reports')}</h2>
            <p className="text-gray-600 mt-1">إنتاج وتصدير التقارير المالية والإحصائية المتقدمة</p>
          </div>
          {reportData && (
            <Button onClick={exportReport} variant="outline" className="bg-green-50 hover:bg-green-100 border-green-200">
              <BarChart3 className="h-4 w-4 mr-2" />
              📤 {t('export')} CSV
            </Button>
          )}
        </div>
      </div>

      {/* Report Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Settings className="h-5 w-5 ml-2" />
            ⚙️ إعدادات التقرير المتقدمة
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Basic Settings Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="reportType">📋 نوع التقرير</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue placeholder="اختر نوع التقرير" />
                </SelectTrigger>
                <SelectContent>
                  {getReportTypeOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {reportType !== 'aging' && (
              <>
                <div>
                  <Label htmlFor="startDate">📅 {t('from')}</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="text-right"
                  />
                </div>

                <div>
                  <Label htmlFor="endDate">📅 {t('to')}</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="text-right"
                  />
                </div>
              </>
            )}
          </div>

          {/* Advanced Settings for Admin Users */}
          {isAdminUser && (
            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">🔧 إعدادات متقدمة (للمديرين والمحاسبين)</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agencyFilter">🏢 فلتر الوكالات</Label>
                  <Select value={selectedAgencies} onValueChange={setSelectedAgencies}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الوكالات" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">🌐 جميع الوكالات</SelectItem>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>
                          🏢 {agency.name} - {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="groupBy">📊 طريقة العرض</Label>
                  <Select value={groupByAgency.toString()} onValueChange={(value) => setGroupByAgency(value === 'true')}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر طريقة العرض" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">🏢 مجمع حسب الوكالة</SelectItem>
                      <SelectItem value="false">📋 مجمع عام</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex justify-center pt-4">
            <Button 
              onClick={generateReport} 
              disabled={loading || (!startDate || !endDate) && reportType !== 'aging'}
              className="w-full md:w-auto bg-brand-600 hover:bg-brand-700 px-8 py-3"
              size="lg"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  جاري الإنتاج...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  🔄 {t('generateReport')}
                </>
              )}
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="h-5 w-5 text-red-500 mr-2" />
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Report Display with Agency Breakdown */}
      {reportData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center">
                <BarChart3 className="h-5 w-5 ml-2" />
                {reportData.title}
              </span>
              <div className="flex items-center space-x-2">
                {reportData.period && (
                  <Badge variant="secondary">{reportData.period}</Badge>
                )}
                {reportData.group_by_agency && (
                  <Badge variant="outline" className="bg-brand-50 text-brand-700">
                    📊 مجمع حسب الوكالة
                  </Badge>
                )}
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Agency-Grouped Reports Display */}
            {reportData.group_by_agency && reportData.agencies_data ? (
              <div className="space-y-8">
                {/* Grand Totals Summary */}
                <div className="bg-gradient-to-r from-brand-50 to-brand-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-brand-900 mb-4 text-center">
                    📊 الإجمالي العام - جميع الوكالات
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-brand-800">إجمالي المبيعات</p>
                      <p className="text-3xl font-bold text-brand-900">
                        {reportData.grand_totals.sales?.toLocaleString() || reportData.grand_totals.amount?.toLocaleString() || 0} دج
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-brand-800">
                        {reportType === 'aging' ? 'عدد الفواتير' : 'عدد الحجوزات'}
                      </p>
                      <p className="text-3xl font-bold text-brand-900">
                        {reportData.grand_totals.bookings || reportData.grand_totals.count || 0}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm font-medium text-brand-800">عدد الوكالات</p>
                      <p className="text-3xl font-bold text-brand-900">
                        {reportData.agencies_data.length}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Individual Agency Reports */}
                <div className="space-y-6">
                  {reportData.agencies_data.map((agency, agencyIndex) => (
                    <div key={agencyIndex} className="border rounded-lg overflow-hidden">
                      {/* Agency Header */}
                      <div className="bg-gray-50 px-6 py-4 border-b">
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-semibold text-gray-900">
                            🏢 {agency.agency_name}
                          </h3>
                          <div className="flex space-x-4 text-sm">
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                              💰 {agency.totals?.sales?.toLocaleString() || agency.totals?.amount?.toLocaleString() || 0} دج
                            </span>
                            <span className="bg-brand-100 text-brand-800 px-3 py-1 rounded-full">
                              📋 {agency.totals?.bookings || agency.totals?.count || 0} 
                              {reportType === 'aging' ? ' فاتورة' : ' حجز'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Agency Data */}
                      <div className="p-6">
                        {reportType === 'aging' ? (
                          // Aging Report - show invoices table
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-right">👤 العميل</TableHead>
                                <TableHead className="text-right">📄 رقم الفاتورة</TableHead>
                                <TableHead className="text-right">💰 المبلغ (دج)</TableHead>
                                <TableHead className="text-right">⏰ عدد الأيام</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {agency.invoices.map((invoice, index) => (
                                <TableRow key={index} className="hover:bg-gray-50">
                                  <TableCell className="font-medium text-right">{invoice.client}</TableCell>
                                  <TableCell className="text-right">{invoice.invoice}</TableCell>
                                  <TableCell className="text-right text-red-600 font-semibold">
                                    {invoice.amount.toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <Badge variant={invoice.days > 30 ? 'destructive' : invoice.days > 15 ? 'default' : 'secondary'}>
                                      {invoice.days} يوم
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          // Sales Reports - show periods table
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="text-right">
                                  {reportType === 'monthly_sales' ? '📅 الشهر' : '📅 التاريخ'}
                                </TableHead>
                                <TableHead className="text-right">💰 المبيعات (دج)</TableHead>
                                <TableHead className="text-right">📋 عدد الحجوزات</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(agency.periods || []).map((period, index) => (
                                <TableRow key={index} className="hover:bg-gray-50">
                                  <TableCell className="font-medium text-right">
                                    {period.month || period.date || period.period}
                                  </TableCell>
                                  <TableCell className="text-right text-green-600 font-semibold">
                                    {period.sales.toLocaleString()}
                                  </TableCell>
                                  <TableCell className="text-right">{period.bookings}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              // Traditional Report Display (non-grouped)
              <div>
                {reportType === 'daily_sales' || reportType === 'monthly_sales' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <p className="text-sm font-medium text-green-800">إجمالي المبيعات</p>
                            <p className="text-2xl font-bold text-green-900">
                              {reportData.totals.sales.toLocaleString()} دج
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-r from-brand-50 to-brand-50 border-brand-200">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <p className="text-sm font-medium text-brand-800">إجمالي الحجوزات</p>
                            <p className="text-2xl font-bold text-brand-900">
                              {reportData.totals.bookings}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">
                            {reportType === 'monthly_sales' ? '📅 الشهر' : '📅 التاريخ'}
                          </TableHead>
                          <TableHead className="text-right">💰 المبيعات (دج)</TableHead>
                          <TableHead className="text-right">📋 عدد الحجوزات</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.data.map((row, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-right">
                              {reportType === 'monthly_sales' ? row.month : row.date}
                            </TableCell>
                            <TableCell className="text-right text-green-600 font-semibold">
                              {row.sales.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">{row.bookings}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : reportType === 'aging' ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <p className="text-sm font-medium text-orange-800">عدد الفواتير المعلقة</p>
                            <p className="text-2xl font-bold text-orange-900">
                              {reportData.totals.count}
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                      
                      <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-200">
                        <CardContent className="p-4">
                          <div className="text-center">
                            <p className="text-sm font-medium text-red-800">إجمالي المبلغ المعلق</p>
                            <p className="text-2xl font-bold text-red-900">
                              {reportData.totals.amount.toLocaleString()} دج
                            </p>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-right">👤 العميل</TableHead>
                          <TableHead className="text-right">📄 رقم الفاتورة</TableHead>
                          <TableHead className="text-right">💰 المبلغ (دج)</TableHead>
                          <TableHead className="text-right">⏰ عدد الأيام</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.data.map((row, index) => (
                          <TableRow key={index} className="hover:bg-gray-50">
                            <TableCell className="font-medium text-right">{row.client}</TableCell>
                            <TableCell className="text-right">{row.invoice}</TableCell>
                            <TableCell className="text-right text-red-600 font-semibold">
                              {row.amount.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right">
                              <Badge variant={row.days > 30 ? 'destructive' : row.days > 15 ? 'default' : 'secondary'}>
                                {row.days} يوم
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                ) : reportType === 'summary' ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-green-800 mb-3">💰 إجمالي المبيعات</h3>
                        <div className="text-center">
                          <p className="text-3xl font-bold text-green-900">
                            {reportData.data.sales.toLocaleString()} دج
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-r from-brand-50 to-brand-50 border-brand-200">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-brand-800 mb-3">📋 إجمالي الحجوزات</h3>
                        <div className="text-center">
                          <p className="text-3xl font-bold text-brand-900">
                            {reportData.data.bookings}
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-r from-purple-50 to-violet-50 border-purple-200">
                      <CardContent className="p-6">
                        <h3 className="font-semibold text-purple-800 mb-3">📄 إجمالي الفواتير</h3>
                        <div className="text-center">
                          <p className="text-3xl font-bold text-purple-900">
                            {reportData.data.invoices}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// User Management Component (Super Admin Only)

export default ReportsManagement;
